import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '../App.jsx';
import { api } from '../lib/api.js';
import { day, inr, num, qty } from '../lib/format.js';
import { thisMonth } from '../lib/dates.js';
import { Alert, Card, CardSkeleton, Empty, StatCard, useAsync } from '../components/ui.jsx';
import PeriodPicker from '../components/PeriodPicker.jsx';
import ExportButton, { periodSlug, periodText } from '../components/ExportButton.jsx';

const GROUPS = [
  ['invoice', 'By document'], ['customer', 'By customer'], ['item', 'By item'], ['hsn', 'By HSN'],
  ['godown', 'By godown'], ['day', 'By day'], ['month', 'By month'],
];
const TAX = [['taxable', 'Taxable'], ['cgst', 'CGST'], ['sgst', 'SGST'], ['igst', 'IGST'], ['cess', 'Cess']];

/** What was billed in a period, net of credit notes, grouped the way it is being asked about. */
export default function SalesRegister() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [period, setPeriod] = useState(thisMonth);
  const [by, setBy] = useState('invoice');
  const [withCn, setWithCn] = useState(true);
  const { from, to } = period;
  const reg = useAsync(() => api.get(
    `/api/reports/sales-register?entity_id=${entityId}&from=${from}&to=${to}&by=${by}&credit_notes=${withCn}`),
  [entityId, from, to, by, withCn]);

  const data = reg.data && reg.data.by === by ? reg.data : null;
  const isDocs = by === 'invoice';
  const hasQty = by === 'item' || by === 'hsn';
  const label = { customer: 'Customer', item: 'Item', hsn: 'HSN', godown: 'Godown', day: 'Day', month: 'Month' }[by];

  const columns = isDocs
    ? [
      { key: 'kind', header: 'Document' }, { key: 'number', header: 'Number' }, { key: 'date', header: 'Date', type: 'date' },
      { key: 'buyer_name', header: 'Buyer' }, { key: 'buyer_gstin', header: 'GSTIN' }, { key: 'place_of_supply', header: 'POS' },
      { key: 'godown', header: 'Godown' },
      ...TAX.map(([k, h]) => ({ key: k, header: h, type: 'money', total: true })),
      { key: 'tcs', header: 'TCS', type: 'money', total: true }, { key: 'total', header: 'Total', type: 'money', total: true },
      { key: 'tally_sync_status', header: 'Tally' },
    ]
    : [
      { key: 'label', header: label },
      ...(by === 'customer' ? [{ key: 'gstin', header: 'GSTIN' }] : []),
      ...(hasQty ? [{ key: 'qty', header: 'Quantity', type: 'number' }, { key: 'uom', header: 'Unit' }] : []),
      { key: 'documents', header: hasQty ? 'Lines' : 'Invoices', type: 'int', total: true },
      ...(!hasQty ? [{ key: 'credit_notes', header: 'Credit notes', type: 'int', total: true }] : []),
      ...TAX.map(([k, h]) => ({ key: k, header: h, type: 'money', total: true })),
      ...(!hasQty ? [{ key: 'tcs', header: 'TCS', type: 'money', total: true }] : []),
      { key: 'total', header: 'Total', type: 'money', total: true },
    ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Sales register</h1>
          <div className="page-sub">
            Everything billed in the period from InvIT's own documents, with credit notes taken off, by document or
            grouped. It agrees with GSTR-1 for the same period.
          </div>
        </div>
        <div className="inline">
          <PeriodPicker value={period} onChange={setPeriod} />
          <ExportButton disabled={!data} build={() => ({
            filename: `sales-register-${by}-${periodSlug(period)}`,
            title: `Sales register — ${GROUPS.find(([k]) => k === by)[1].toLowerCase()}`,
            subtitle: `${periodText(period)}${withCn ? ' · net of credit notes' : ' · invoices only'}`,
            sheets: [{
              name: GROUPS.find(([k]) => k === by)[1],
              columns,
              rows: data.rows.map((r) => ({ ...r, kind: r.doc_type === 'credit_note' ? 'Credit note' : 'Invoice' })),
            }],
          })} />
        </div>
      </div>

      <div className="inline" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div className="segmented" role="group" aria-label="Group by">
          {GROUPS.map(([k, l]) => <button key={k} aria-pressed={by === k} onClick={() => setBy(k)}>{l}</button>)}
        </div>
        <div className="segmented" role="group" aria-label="Credit notes">
          <button aria-pressed={withCn} onClick={() => setWithCn(true)}>Net of credit notes</button>
          <button aria-pressed={!withCn} onClick={() => setWithCn(false)}>Invoices only</button>
        </div>
      </div>

      <Alert onRetry={reg.reload}>{reg.error}</Alert>
      {!data && reg.loading && <CardSkeleton lines={6} />}

      {data && (
        <>
          <div className="grid kpi" style={{ marginBottom: 16 }}>
            <StatCard label="Taxable value" value={inr(data.totals.taxable, 0)} foot={periodText(period)} />
            <StatCard label="Tax" value={inr(num(data.totals.cgst) + num(data.totals.sgst) + num(data.totals.igst) + num(data.totals.cess), 0)}
              foot={`IGST ${inr(data.totals.igst, 0)} · CGST+SGST ${inr(num(data.totals.cgst) + num(data.totals.sgst), 0)}`} />
            <StatCard label="Billed" value={inr(data.totals.total, 0)} foot={withCn ? 'net of credit notes' : 'invoices only'} />
            <StatCard label={isDocs ? 'Documents' : 'Groups'} value={data.rows.length}
              foot={isDocs ? `${data.rows.filter((r) => r.doc_type === 'credit_note').length} credit notes` : label.toLowerCase()} />
          </div>

          <Card pad={data.rows.length === 0}>
            {data.rows.length === 0 ? <Empty title="Nothing billed in this period" /> : (
              <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
                <table>
                  <thead>
                    <tr>
                      {isDocs
                        ? <><th>Document</th><th>Date</th><th>Buyer</th></>
                        : <><th>{label}</th>{hasQty && <th className="r">Quantity</th>}<th className="r">{hasQty ? 'Lines' : 'Invoices'}</th></>}
                      {TAX.map(([k, h]) => <th key={k} className="r">{h}</th>)}
                      <th className="r">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={isDocs ? `${r.doc_type}-${r.id}` : `${r.label}-${i}`}>
                        {isDocs ? (
                          <>
                            <td className="mono">
                              <Link to={r.doc_type === 'invoice' ? `/invoices/${r.id}` : `/credit-notes/${r.id}`}>{r.number}</Link>
                              {r.doc_type === 'credit_note' && <div className="sub">credit note</div>}
                            </td>
                            <td>{day(r.date)}</td>
                            <td>{r.buyer_name}<div className="sub mono">{r.buyer_gstin || 'Unregistered'}</div></td>
                          </>
                        ) : (
                          <>
                            <td>{r.label || '—'}{by === 'customer' && r.gstin && <div className="sub mono">{r.gstin}</div>}</td>
                            {hasQty && <td className="r num">{r.qty ? qty(r.qty, r.uom) : '—'}</td>}
                            <td className="r num">{r.documents}{!hasQty && r.credit_notes ? <div className="sub">{r.credit_notes} CN</div> : null}</td>
                          </>
                        )}
                        {TAX.map(([k]) => (
                          <td key={k} className="r num" style={{ color: num(r[k]) < 0 ? 'var(--critical-text)' : undefined }}>
                            {num(r[k]) ? inr(r[k], 0) : '—'}
                          </td>
                        ))}
                        <td className="r num"><b>{inr(r.total, 0)}</b></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={isDocs ? 3 : (hasQty ? 3 : 2)} className="r">Total</th>
                      {TAX.map(([k]) => <th key={k} className="r num">{inr(data.totals[k], 0)}</th>)}
                      <th className="r num">{inr(data.totals.total, 0)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
